/**
 * adsFlow.ts — the six steps of an ad week (Research → Ideas → Ads → Review → Live → Results) and how their status is
 * worked out from plain counts. Pure, so the server fills the counts and the screen only draws the answer.
 *
 * A step is exactly one of: done · you (a person has to act) · running · failed · waiting. Nothing is guessed: a step is
 * "done" only when the records for it exist this week.
 */
import { z } from "zod";

export const FLOW_STEPS = ["research", "ideas", "ads", "review", "live", "results"] as const;
export type FlowStepId = (typeof FLOW_STEPS)[number];
export const FLOW_STATUSES = ["done", "you", "running", "failed", "waiting"] as const;
export type FlowStatus = (typeof FLOW_STATUSES)[number];

export const FLOW_LABEL: Record<FlowStepId, string> = { research: "Research", ideas: "Concepts", ads: "Ads", review: "Review", live: "Live", results: "Results" };

export const flowCountsSchema = z.object({
  /** Competitor ads for this product found or seen again this week. */
  researchAds: z.number().int().min(0),
  concepts: z.number().int().min(0),
  conceptsPicked: z.number().int().min(0),
  /** Ads written this week that are still in play (not replaced, not archived). */
  ads: z.number().int().min(0),
  inReview: z.number().int().min(0),
  approved: z.number().int().min(0),
  /** Approved Google ads already put in an Editor file. */
  exported: z.number().int().min(0),
  /** Our own results rows for last week. */
  resultRows: z.number().int().min(0),
  /** The latest automatic or manual run this week. */
  run: z.object({ status: z.enum(["running", "done", "failed"]), error: z.string().nullable() }).nullable(),
});
export type FlowCounts = z.infer<typeof flowCountsSchema>;

export const flowStepSchema = z.object({
  id: z.enum(FLOW_STEPS),
  label: z.string(),
  status: z.enum(FLOW_STATUSES),
  /** The big number on the card; null when the step has nothing to count yet. */
  count: z.number().int().nullable(),
  /** What the number counts, e.g. "to review". */
  unit: z.string(),
  /** When this step last ran, and with which models ("Grok + Claude"). Null for steps a person does (v92). */
  lastRunAt: z.string().nullable().default(null),
  models: z.string().nullable().default(null),
});
export type FlowStep = z.infer<typeof flowStepSchema>;

export const flowSchema = z.object({
  weekStart: z.string(),
  counts: flowCountsSchema,
  steps: z.array(flowStepSchema).length(FLOW_STEPS.length),
  headline: z.string(),
  /** What the one big button does. */
  action: z.enum(["review", "none"]),
  /** The plain sentence when the latest run failed. */
  problem: z.string().nullable(),
  /** The latest run this week: when, which models, the rules it was told. Null when there was none (v92). */
  lastRun: z.object({ at: z.string(), finishedAt: z.string().nullable(), model: z.string().nullable(), status: z.enum(["running", "done", "failed"]), rules: z.array(z.string()) }).nullable().default(null),
});
export type Flow = z.infer<typeof flowSchema>;

const plural = (n: number, one: string, many: string) => (n === 1 ? one : many);

export function buildSteps(c: FlowCounts): FlowStep[] {
  const running = c.run?.status === "running";
  const failed = c.run?.status === "failed";
  const step = (id: FlowStepId, status: FlowStatus, count: number | null, unit: string): FlowStep => ({ id, label: FLOW_LABEL[id], status, count, unit, lastRunAt: null, models: null });

  const research = step("research", c.researchAds > 0 ? "done" : "waiting", c.researchAds > 0 ? c.researchAds : null, plural(c.researchAds, "competitor ad", "competitor ads"));
  const ideas = step("ideas", c.concepts > 0 ? "done" : running ? "running" : failed ? "failed" : "waiting", c.concepts > 0 ? c.concepts : null, c.conceptsPicked > 0 ? `${plural(c.concepts, "idea", "ideas")}, ${c.conceptsPicked} picked` : plural(c.concepts, "idea", "ideas"));
  const ads = step("ads", c.ads > 0 ? "done" : running ? "running" : failed ? "failed" : "waiting", c.ads > 0 ? c.ads : null, c.ads === 1 ? "ad built" : "ads built");
  const review = step("review", c.inReview > 0 ? "you" : c.ads > 0 ? "done" : "waiting", c.inReview > 0 ? c.inReview : c.ads > 0 ? 0 : null, c.inReview > 0 ? "to review" : "left to review");
  const live = step("live", c.approved > 0 ? "done" : "waiting", c.approved > 0 ? c.approved : null, c.exported > 0 ? `approved, ${c.exported} in a file` : "approved");
  const results = step("results", c.resultRows > 0 ? "done" : "waiting", c.resultRows > 0 ? c.resultRows : null, plural(c.resultRows, "week of results", "rows of results"));
  return [research, ideas, ads, review, live, results];
}

export function buildFlow(weekStart: string, c: FlowCounts): Flow {
  const steps = buildSteps(c);
  const failedRun = c.run?.status === "failed" && c.ads === 0;
  const problem = failedRun ? (c.run?.error?.trim() || "The weekly run stopped before it finished.") : null;
  let headline: string;
  let action: Flow["action"] = "none";
  if (c.inReview > 0) {
    headline = `${c.inReview} ${plural(c.inReview, "ad is", "ads are")} waiting for you`;
    action = "review";
  } else if (c.run?.status === "running") headline = "The system is working on this week's ads";
  else if (failedRun) headline = "This week's run hit a problem";
  else if (c.ads > 0) headline = "Nothing is waiting for you";
  else headline = "No ads yet this week";
  return { weekStart, counts: c, steps, headline, action, problem, lastRun: null };
}

/** The strip shows one vocabulary for Ads and Social: done · waiting for you · running · failed · not yet. */
export function flowStatusToCell(s: FlowStatus): "done" | "waiting" | "running" | "failed" | "not_yet" {
  return s === "you" ? "waiting" : s === "waiting" ? "not_yet" : s;
}
